import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { resolveMutationErrorMessage } from "@/shared/api/error-message";
import { useArchiveTaxRate } from "./use-archive-tax-rate";
import type { StripeTaxRate } from "./types";

interface ArchiveTaxRateDialogProps {
  readonly rate: StripeTaxRate | null;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSuccess: () => void;
}

export function ArchiveTaxRateDialog({
  rate,
  open,
  onOpenChange,
  onSuccess,
}: ArchiveTaxRateDialogProps) {
  const { t } = useTranslation();
  const archive = useArchiveTaxRate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  if (!rate) return null;

  const handleClose = (next: boolean) => {
    if (!next && archive.isPending) return;
    onOpenChange(next);
  };

  const handleConfirm = () => {
    setError(null);
    archive.mutate(rate.id, {
      onSuccess: () => onSuccess(),
      onError: (err) => {
        setError(
          resolveMutationErrorMessage(err, t, {
            forbiddenKey: "catalog.errors.forbidden",
            serverPrefixKey: "catalog.errors.serverPrefix",
            genericKey: "catalog.taxRates.archive.error",
          }),
        );
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="archive-tax-rate-dialog">
        <DialogHeader>
          <DialogTitle>
            {t("catalog.taxRates.archive.title", { name: rate.displayName })}
          </DialogTitle>
          <DialogDescription>{t("catalog.taxRates.archive.description")}</DialogDescription>
        </DialogHeader>

        {error && (
          <p
            data-testid="archive-tax-rate-error"
            role="alert"
            className="font-sans text-sm font-medium text-destructive"
          >
            {error}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={archive.isPending}>
            {t("catalog.actions.cancel")}
          </Button>
          <Button
            data-testid="confirm-archive-tax-rate-button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={archive.isPending}
          >
            {archive.isPending
              ? t("catalog.taxRates.archive.pending")
              : t("catalog.taxRates.actions.archive")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
